import { Link } from "react-router-dom";
import { Leaf, ShoppingBag } from "lucide-react";
import Seo from "@/components/Seo";
import { CouponBox, QtyStepper } from "@/components/CartDrawer";
import { useCart } from "@/context/CartContext";
import { money } from "@/lib/api";

export default function CartPage() {
    const { items, subtotal, discount, total, coupon } = useCart();

    return (
        <div className="pt-24 md:pt-32 pb-24" data-testid="cart-page">
            <Seo
                title="Your Bag — District Greenz DMV"
                description="Review your bag, apply a coupon, and check out with local delivery or store pickup."
                path="/cart"
            />
            <div className="container-x max-w-5xl">
                <p className="eyebrow text-gold">Your Bag</p>
                <h1 className="mt-3 font-display text-4xl md:text-6xl font-extrabold text-bone">
                    {items.length ? "Almost yours." : "Nothing in here yet."}
                </h1>

                {items.length === 0 ? (
                    <div className="mt-12 flex flex-col items-center gap-5 rounded-3xl border border-leaf/15 bg-moss/40 px-8 py-20 text-center" data-testid="cart-empty-state">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full border border-leaf/25 text-leaf">
                            <ShoppingBag size={24} />
                        </div>
                        <p className="text-sage">The menu is stacked — go find something good.</p>
                        <Link to="/shop" data-testid="cart-empty-shop-cta" className="rounded-full bg-leaf px-8 py-4 font-bold text-ink hover:bg-leaf-bright">
                            Shop the Menu
                        </Link>
                    </div>
                ) : (
                    <div className="mt-12 grid gap-10 lg:grid-cols-12">
                        <div className="lg:col-span-7 divide-y divide-leaf/10 rounded-3xl border border-leaf/12 bg-moss/30 px-6">
                            {items.map((line) => (
                                <div key={line.key} className="flex gap-4 py-5" data-testid="cart-page-line">
                                    <div className="h-24 w-24 shrink-0 overflow-hidden rounded-xl border border-leaf/15 bg-moss">
                                        {line.image ? (
                                            <img src={line.image} alt={line.name} className="h-full w-full object-cover" />
                                        ) : (
                                            <div className="flex h-full w-full items-center justify-center text-leaf/50">
                                                <Leaf size={20} />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-1 flex-col justify-between min-w-0">
                                        <div>
                                            <Link to={`/product/${line.slug}`} className="font-display font-bold hover:text-leaf-bright transition-colors line-clamp-2">
                                                {line.name}
                                            </Link>
                                            {line.options?.length > 0 && (
                                                <p className="mt-1 text-xs text-sage">{line.options.map((o) => `${o.name}: ${o.value}`).join(" · ")}</p>
                                            )}
                                        </div>
                                        <div className="flex items-center justify-between gap-3">
                                            <QtyStepper line={line} testidPrefix="cart-page" />
                                            <span className={`font-bold ${line.on_sale ? "text-leaf-bright" : "text-bone"}`}>
                                                {money(line.unit_price * line.quantity)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="lg:col-span-5">
                            <div className="sticky top-28 space-y-5 rounded-3xl border border-leaf/15 bg-moss/40 p-6" data-testid="cart-summary">
                                <h2 className="font-display text-xl font-extrabold text-bone">Order Summary</h2>
                                <CouponBox />
                                <div className="space-y-2 border-t border-leaf/12 pt-4 text-sm">
                                    {total < 40 && (
                                        <p className="rounded-xl border border-gold/30 bg-gold/5 p-3 text-xs text-gold" data-testid="cart-minimum-note">
                                            Minimum order is $40 — add {money(40 - total)} more to check out.
                                        </p>
                                    )}
                                    <div className="flex justify-between text-sage">
                                        <span>Subtotal</span>
                                        <span data-testid="cart-page-subtotal">{money(subtotal)}</span>
                                    </div>
                                    {discount > 0 && (
                                        <div className="flex justify-between text-leaf-bright" data-testid="cart-page-discount">
                                            <span>Discount ({coupon?.code})</span>
                                            <span>-{money(discount)}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between border-t border-leaf/12 pt-3 font-display text-xl font-extrabold text-bone">
                                        <span>Total</span>
                                        <span data-testid="cart-page-total">{money(total)}</span>
                                    </div>
                                </div>
                                <Link
                                    to="/checkout"
                                    data-testid="cart-page-checkout-cta"
                                    onClick={(e) => { if (total < 40) e.preventDefault(); }}
                                    className={`block w-full rounded-full py-4 text-center font-bold transition-colors ${
                                        total < 40
                                            ? "cursor-not-allowed bg-moss text-sage"
                                            : "bg-leaf text-ink hover:bg-leaf-bright"
                                    }`}
                                >
                                    {total < 40 ? `Add ${money(40 - total)} more to continue` : "Proceed to Checkout"}
                                </Link>
                                <Link to="/shop" data-testid="cart-page-continue" className="block text-center font-mono text-xs uppercase tracking-widest text-sage hover:text-leaf-bright">
                                    Continue shopping
                                </Link>
                                <p className="text-center text-[11px] leading-relaxed text-sage/80">
                                    Cash on delivery or pay at pickup. Valid 21+ ID required at hand-off.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
